import { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Tooltip, 
  Avatar,
  Button,
  Divider,
  Chip,
  Popover,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import notificationService from '../../services/notificationService';
import { frostedPopoverSx } from '../../styles/tokens';
import Logo from '../ui/Logo';

export default function Layout({ children, mode, toggleTheme }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Nav item styling with active-route highlighting.
  const navItemSx = (path, exact = false) => {
    const active = exact ? location.pathname === path : location.pathname.startsWith(path);
    return {
      color: active ? 'var(--color-primary)' : 'var(--color-muted)',
      fontWeight: active ? 700 : 600,
      px: 1.75,
      borderRadius: '10px',
      bgcolor: active ? 'color-mix(in oklab, var(--color-primary) 10%, transparent)' : 'transparent',
      '&:hover': { bgcolor: 'color-mix(in oklab, var(--color-primary) 8%, transparent)', color: 'var(--color-fg)' },
      transition: 'all 0.2s',
    };
  };

  // Floating nav: switches to an inset "pill" once the page is scrolled.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 24);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const [anchorEl, setAnchorEl] = useState(null);

  // ── Notifications state ──────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [bellAnchorEl, setBellAnchorEl] = useState(null);
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const data = await notificationService.getNotifications();
      setNotifications(data);
    } catch (_) { /* silent */ }
  }, [user]);

  // Poll every 30 s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleOpenBell = (e) => setBellAnchorEl(e.currentTarget);
  const handleCloseBell = () => setBellAnchorEl(null);

  const handleMarkAllRead = async () => {
    await notificationService.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const handleMarkOneRead = async (id) => {
    await notificationService.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const notifIcon = (type) => {
    const base = { fontSize: '0.95rem' };
    if (type === 'deletion_requested')
      return <i aria-hidden className="fas fa-trash-can" style={{ ...base, color: 'var(--color-warning)' }} />;
    if (type === 'deletion_approved')
      return <i aria-hidden className="fas fa-circle-check" style={{ ...base, color: 'var(--color-success)' }} />;
    if (type === 'deletion_rejected')
      return <i aria-hidden className="fas fa-circle-xmark" style={{ ...base, color: 'var(--color-error)' }} />;
    return <i aria-hidden className="fas fa-bell" style={base} />;
  };

  const relativeTime = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };
  // ────────────────────────────────────────────────────────────────

  const handleOpenProfile = (event) => setAnchorEl(event.currentTarget);
  const handleCloseProfile = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getUserInitials = (email) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  // Role → avatar colour, driven entirely by design-system tokens so it
  // adapts to light/dark and stays distinct per role (previously `approver`
  // and `user` both resolved to the same teal).
  const roleAvatarBg = (role) =>
    role === 'admin' ? 'var(--color-accent)' :
    role === 'approver' ? 'var(--color-warning)' :
    role === 'user' ? 'var(--color-primary)' :
    'var(--color-faint)';

  const username = user?.email ? user.email.split('@')[0] : '';
  const displayName = username
    ? username
        .split(/[._-]/)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ')
    : 'User';

  const profileOpen = Boolean(anchorEl);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* Ambient background — biodimension.in mood: dot-grid texture plus
          soft teal/clay blobs. Fixed so it doesn't scroll; sits behind
          everything and also enriches the nav's frosted backdrop. */}
      <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Box className="bg-dotfield" sx={{ position: 'absolute', inset: 0 }} />
        <Box
          className="blob animate-float-slow"
          sx={{ width: 460, height: 460, top: -140, left: -100, background: 'var(--color-teal)', opacity: 0.3 }}
        />
        <Box
          className="blob animate-float-slower"
          sx={{ width: 400, height: 400, bottom: -160, right: -110, background: 'var(--color-clay)', opacity: 0.25 }}
        />
      </Box>
      <AppBar
        position="fixed"
        elevation={0}
        className="glass-nav"
        sx={{
          top: scrolled ? { xs: 8, md: 12 } : 0,
          left: scrolled ? { xs: 8, md: 16 } : 0,
          right: scrolled ? { xs: 8, md: 16 } : 0,
          width: 'auto',
          background: scrolled ? 'var(--color-glass-bg)' : 'var(--color-nav-bg)',
          // Frosted glass — declared inline so no stylesheet processing or
          // override can strip it (backup for the .glass-nav class).
          backdropFilter: 'blur(48px) saturate(180%)',
          WebkitBackdropFilter: 'blur(48px) saturate(180%)',
          color: 'var(--color-fg)',
          boxShadow: scrolled ? 'var(--shadow-e2)' : 'var(--shadow-e1)',
          border: '1px solid',
          borderColor: scrolled ? 'var(--color-glass-border)' : 'transparent',
          borderBottomColor: scrolled ? 'var(--color-glass-border)' : 'var(--color-outline)',
          borderRadius: scrolled ? 'var(--radius-lg)' : '0px',
          transition: [
            'top .45s var(--ease-out-soft)',
            'left .45s var(--ease-out-soft)',
            'right .45s var(--ease-out-soft)',
            'border-radius .45s var(--ease-out-soft)',
            'background .45s var(--ease-out-soft)',
            'box-shadow .45s var(--ease-out-soft)',
            'border-color .45s var(--ease-out-soft)',
          ].join(', '),
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <Toolbar
          sx={{
            position: 'relative',
            minHeight: { xs: scrolled ? 52 : 56, sm: scrolled ? 56 : 64 },
            transition: 'min-height .45s var(--ease-out-soft)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Logo size={30} />
            <Typography
              variant="h6"
              noWrap
              component="div"
              className="font-display text-accent-gradient"
              sx={{ fontWeight: 800 }}
            >
              Cryopreservation Tool
            </Typography>
          </Box>

          {/* Primary navigation — centered in the bar on md+, inline on small screens */}
          <Box
            component="nav"
            sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              ml: { xs: 2, md: 0 },
              position: { md: 'absolute' },
              left: { md: '50%' },
              transform: { md: 'translateX(-50%)' },
            }}
          >
            <Button size="small" onClick={() => navigate('/app')} sx={navItemSx('/app', true)}>
              Records
            </Button>
            <Button size="small" onClick={() => navigate('/app/dashboard')} sx={navItemSx('/app/dashboard')}>
              Dashboard
            </Button>
            {user?.role === 'admin' && (
              <>
                <Button size="small" onClick={() => navigate('/app/audit')} sx={navItemSx('/app/audit')}>
                  Audit
                </Button>
                <Button size="small" onClick={() => navigate('/app/users')} sx={navItemSx('/app/users')}>
                  Users
                </Button>
              </>
            )}
          </Box>

          {/* Spacer pushes action cluster to the right */}
          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              sx={{
                mr: 1,
                color: 'var(--color-primary)',
                bgcolor: 'color-mix(in oklab, var(--color-primary) 8%, transparent)',
                '&:hover': {
                  bgcolor: 'color-mix(in oklab, var(--color-primary) 16%, transparent)',
                },
                borderRadius: '10px',
                transition: 'all 0.2s',
              }}
            >
              <i aria-hidden className={`fas ${mode === 'light' ? 'fa-moon' : 'fa-sun'}`} style={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>

          {/* Bell notification button */}
          {user && (
            <>
              <Tooltip title="Notifications">
                <IconButton
                  onClick={handleOpenBell}
                  aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
                  sx={{
                    mr: 1.5,
                    color: 'var(--color-primary)',
                    bgcolor: unreadCount > 0
                      ? 'color-mix(in oklab, var(--color-primary) 10%, transparent)'
                      : 'transparent',
                    '&:hover': {
                      bgcolor: 'color-mix(in oklab, var(--color-primary) 16%, transparent)',
                    },
                    borderRadius: '10px',
                    transition: 'all 0.2s',
                  }}
                >
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={9}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
                  >
                    <i aria-hidden className="fas fa-bell" style={{ fontSize: '1.15rem' }} />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Notifications Popover */}
              <Popover
                open={Boolean(bellAnchorEl)}
                anchorEl={bellAnchorEl}
                onClose={handleCloseBell}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    sx: {
                      ...frostedPopoverSx,
                      width: 360,
                      borderRadius: 'var(--radius-md)',
                      mt: 1.5,
                      overflow: 'hidden',
                    }
                  }
                }}
              >
                {/* Header */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Notifications {unreadCount > 0 && (
                      <Chip label={unreadCount} color="error" size="small" sx={{ ml: 1, fontSize: '0.7rem', height: 18 }} />
                    )}
                  </Typography>
                  {unreadCount > 0 && (
                    <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: '0.75rem', textTransform: 'none', p: 0.5 }}>
                      Mark all read
                    </Button>
                  )}
                </Box>
                <Divider />

                {/* Notification list */}
                <List disablePadding sx={{ maxHeight: 400, overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <i aria-hidden className="fas fa-bell" style={{ fontSize: 34, opacity: 0.2, marginBottom: 8, display: 'block' }} />
                      <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
                    </Box>
                  ) : (
                    notifications.map((notif) => (
                      <ListItem
                        key={notif.id}
                        alignItems="flex-start"
                        onClick={() => {
                          if (!notif.is_read) handleMarkOneRead(notif.id);
                          // Deep-link: jump to the records list filtered to this
                          // record's cell line so the user can act on it.
                          if (notif.cell_line_name) {
                            navigate(`/app?q=${encodeURIComponent(notif.cell_line_name)}`);
                            handleCloseBell();
                          }
                        }}
                        sx={{
                          cursor: notif.cell_line_name || !notif.is_read ? 'pointer' : 'default',
                          bgcolor: notif.is_read
                            ? 'transparent'
                            : 'color-mix(in oklab, var(--color-primary) 6%, transparent)',
                          borderLeft: notif.is_read ? '3px solid transparent' : '3px solid',
                          borderColor: notif.is_read ? 'transparent' : 'var(--color-primary)',
                          '&:hover': { bgcolor: 'color-mix(in oklab, var(--color-primary) 10%, transparent)' },
                          transition: 'background 0.2s',
                          py: 1.5,
                          px: 2,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                          {notifIcon(notif.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={notif.is_read ? 400 : 700} sx={{ lineHeight: 1.3 }}>
                              {notif.title}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, lineHeight: 1.4 }}>
                                {notif.message}
                              </Typography>
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '0.68rem' }}>
                                {relativeTime(notif.created_at)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Popover>
            </>
          )}

          {user && (
            <>
              <Tooltip title="View Profile">
                <IconButton color="inherit" onClick={handleOpenProfile} aria-label="Open profile menu" sx={{ p: 0.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: roleAvatarBg(user?.role),
                      color: 'var(--color-on-primary)',
                      width: 36,
                      height: 36,
                      fontSize: '0.95rem',
                      fontWeight: 'bold',
                      boxShadow: 'var(--shadow-e1)'
                    }}
                  >
                    {getUserInitials(user.email)}
                  </Avatar>
                </IconButton>
              </Tooltip>
              
              {/* Profile Popover - opens near the avatar icon */}
              <Popover
                open={profileOpen}
                anchorEl={anchorEl}
                onClose={handleCloseProfile}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      ...frostedPopoverSx,
                      width: 320,
                      borderRadius: 'var(--radius-md)',
                      p: 2.5,
                      mt: 1.5,
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mb: 2, textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      bgcolor: roleAvatarBg(user?.role),
                      color: 'var(--color-on-primary)',
                      width: 56,
                      height: 56,
                      fontSize: '1.3rem',
                      fontWeight: 'bold',
                      boxShadow: 'var(--shadow-e2)'
                    }}
                  >
                    {getUserInitials(user?.email)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                      {displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                      {user?.email}
                    </Typography>
                  </Box>
                  <Chip 
                    label={
                      user?.role === 'admin' ? 'Administrator' : 
                      user?.role === 'approver' ? 'Approver' : 
                      user?.role === 'user' ? 'Standard User' : 
                      'Viewer'
                    } 
                    color={
                      user?.role === 'admin' ? 'error' : 
                      user?.role === 'approver' ? 'warning' : 
                      user?.role === 'user' ? 'primary' : 
                      'default'
                    } 
                    size="small"
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem', height: 20 }}
                  />
                </Box>
                
                <Divider sx={{ my: 1.5 }} />
                <Button 
                  variant="contained" 
                  color="error" 
                  fullWidth 
                  startIcon={<i aria-hidden className="fas fa-right-from-bracket" />}
                  onClick={handleLogout}
                  sx={{ borderRadius: 'var(--radius-xs)', textTransform: 'none', py: 0.75, fontSize: '0.9rem' }}
                >
                  Sign Out
                </Button>
              </Popover>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: '100%', mt: 8, position: 'relative', zIndex: 1 }}
      >
        {children}
      </Box>
    </Box>
  );
}
